/*
 * Purpose: Compose Forgot Password UI using PasswordWithOtp compound component.
 * Boundary: Layout only; no business logic; delegates state/actions to hook.
 * Invariants:
 * - Consumes object h from useAuthForm with state and handlers.
 * - Uses PhoneNumber component for cell input and PasswordWithOtp for password+OTP.
 * - ASCII only; no Unicode in comments or strings
 */
// Imports
import { useMemo } from 'react';

import Chip from '../authform-chips';
import PhoneNumber from '../../../contact/phonenumber';
import PasswordWithOtp from '../../../password/passwordwithotp';

import { IdField } from '../authform-controls';
import { PhoneContext } from '../../../contact/phonenumber';
import { PasswordWithOtpContext } from '../../../password/passwordwithotp';


// Component ForgotPasswordLayout:
// Stateless layout consuming hook output
// Receives hook output (state + handlers) from useAuthForm
export default function ForgotPasswordLayout(h) {
    // Rendering - outer wrapper and page title
    return (
        <div className='space-y-3 md:space-y-6' aria-labelledby='forgot-password-title'>
            {/* Title - shows Forgot Password */}
            <h1 id='forgot-password-title' className='text-base md:text-xl font-semibold'>
                Forgot Password
            </h1>
            {/* Top status - chips row and hint text */}
            <div className='flex flex-col gap-1'>
                {/* Chip row - required field status */}
                <div className='flex flex-wrap items-center gap-1.5 md:gap-2'>
                    <Chip ok={h.proceedRequired.id} warnWhenMissing>ID</Chip>
                    <Chip ok={h.proceedRequired.cell} warnWhenMissing>Cell</Chip>
                    <Chip ok={h.proceedRequired.passwordWithOtp} warnWhenMissing>Password + OTP</Chip>
                </div>
                {/* Hint - below chips, shows missing list or ready message */}
                <p className={`mt-[4px] text-[12px] ${h.isProceedReady ? 'text-emerald-300' : 'text-orange-300'}`}>
                    {h.isProceedReady ? 'All required fields completed' : `Missing: ${h.missingList().join(', ')}`}
                </p>
            </div>
            {/* Main form container */}
            <div className='rounded-xl bg-neutral-950 ring-1 ring-neutral-800 p-2.5 md:p-5'>
                {/* Form - shared container */}
                <form onSubmit={h.requestPasswordReset} className='grid gap-3 md:gap-4' noValidate>
                    {/* ID Number */}
                    <IdField
                        id='forgot-password-id'
                        value={h.form.idNumber}
                        onChange={(e) => h.setField('idNumber', e.target.value.replace(/\D/g, ''))}
                    />
                    <PhoneContext.Provider value={useMemo(() =>
                        ({ setFullNumber: h.setCellNumber, setIsValid: h.setIsPhoneValid, setCountryCode: h.setCountryCode }),
                        [h.setCellNumber, h.setIsPhoneValid, h.setCountryCode])}>
                        <PhoneNumber
                            enableCountrySelector={true}
                            defaultCountry='ZA'
                            showValidationLabel={false}
                        />
                    </PhoneContext.Provider>
                    <hr className='my-0.5 md:my-1 border-neutral-800' />
                    <PasswordWithOtpContext.Provider value={useMemo(() =>
                        ({ setPasswordWithOtpState: h.setPasswordWithOtpState }),
                        [h.setPasswordWithOtpState])}>
                        <PasswordWithOtp
                            auth={h.auth}
                            type="reset"
                            idNumber={h.form.idNumber}
                            cellNumber={h.cellNumber}
                            isValidID={h.proceedRequired.id}
                            isProceedReadyOtp={h.isProceedReadyOtp}
                            heading='Reset Password'
                         />
                    </PasswordWithOtpContext.Provider>
                    <hr className='my-0.5 md:my-1 border-neutral-800' />
                    {/* Submit or warning */}
                    {h.isProceedReady ? (
                        <button
                            id='btn-update-password'
                            type='submit'
                            className='h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 transition text-white font-medium disabled:opacity-60'
                            disabled={h.isPending}
                        >
                            Update Password
                        </button>
                    ) : (
                        <p className='h-10 flex items-center justify-center rounded-lg bg-orange-900/20 text-sm text-orange-300'>
                            Password reset requirements not met
                        </p>
                    )}
                </form>
                {/* Status message - polite live region */}
                {h.status && (
                    <p className='mt-4 text-sm text-neutral-400' role='status' aria-live='polite'>
                        {h.status}
                    </p>
                )}
            </div>
        </div>
    );
}
